// FILE: components/explore/ExplorePageShell.tsx
"use client";

import { useState, useMemo, useEffect, useRef } from "react";
import ExploreFilterSidebar, {
  MobileFilterSheet,
  DEFAULT_FILTERS,
  type FilterState,
  type CategoryNode,
} from "@/components/explore/ExploreFilterSidebar";
import ExploreHeader from "./ExploreHeader";
import { PathsSection } from "./sections/PathsSection";
import { ExamsSection } from "./sections/ExamsSection";
import { ReviewsSection } from "./sections/ReviewsSection";
import { mapPath, mapExam, mapReview } from "./_shared/mappers";
import type {
  SearchTab,
  SortOption,
  PathItem,
  ExamItem,
  ReviewItem,
  TrendingData,
  CategoriesGrouped,
} from "./_shared/types";

interface Props {
  initialTrending: TrendingData;
  initialCategories: CategoriesGrouped;
}

export default function ExplorePageShell({
  initialTrending,
  initialCategories,
}: Props) {
  // ── Flatten categories cho sidebar ──
  const categories = useMemo<CategoryNode[]>(() => [
    ...(initialCategories.domains ?? []).map((c: any) => ({ ...c, type: "domain" })),
    ...(initialCategories.organizations ?? []).map((c: any) => ({ ...c, type: "organization" })),
    ...(initialCategories.positions ?? []).map((c: any) => ({ ...c, type: "position" })),
    ...(initialCategories.subjects ?? []).map((c: any) => ({ ...c, type: "subject" })),
  ], [initialCategories]);

  // ── State init từ server props (KHÔNG fetch lại) ──
  const [paths, setPaths] = useState<PathItem[]>(() =>
    (initialTrending.paths ?? []).map((p: any, i: number) => mapPath(p, i))
  );
  const [exams, setExams] = useState<ExamItem[]>(() =>
    (initialTrending.exams ?? []).map((e: any) => mapExam(e))
  );
  const [reviews, setReviews] = useState<ReviewItem[]>(() =>
    (initialTrending.reviews ?? []).map((r: any) => mapReview(r))
  );

  const [keyword, setKeyword] = useState("");
  const [activeTab, setActiveTab] = useState<SearchTab>("all");
  const [sort, setSort] = useState<SortOption>("hot");
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // ── Refetch khi filter/keyword/tab/sort thay đổi (debounce 400ms) ──
  const filterTimerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip lần render đầu (đã có data từ server)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    filterTimerRef.current = setTimeout(() => {
      fetchFiltered();
    }, 400);

    return () => {
      if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, keyword, activeTab, sort]);

  async function fetchFiltered() {
    const hasAnyFilter =
      keyword.trim() ||
      filters.domains.length ||
      filters.orgs.length ||
      filters.positions.length ||
      filters.subjects.length ||
      filters.priceType !== "all" ||
      sort !== "hot" ||
      activeTab !== "all";

    // Không có filter → revert về trending cache gốc
    if (!hasAnyFilter) {
      setPaths((initialTrending.paths ?? []).map((p: any, i: number) => mapPath(p, i)));
      setExams((initialTrending.exams ?? []).map((e: any) => mapExam(e)));
      setReviews((initialTrending.reviews ?? []).map((r: any) => mapReview(r)));
      return;
    }

    setLoading(true);
    const params = new URLSearchParams();
    if (keyword.trim()) params.set("q", keyword);
    params.set("tab", activeTab);
    params.set("sort", sort);
    if (filters.domains.length) params.set("domains", filters.domains.join(","));
    if (filters.orgs.length) params.set("orgs", filters.orgs.join(","));
    if (filters.positions.length) params.set("positions", filters.positions.join(","));
    if (filters.subjects.length) params.set("subjects", filters.subjects.join(","));
    if (filters.priceType !== "all") params.set("priceType", filters.priceType);

    try {
      const res = await fetch(`/api/explore?${params.toString()}`);
      const data = await res.json();
      setPaths((data.paths ?? []).map((p: any, i: number) => mapPath(p, i)));
      setExams((data.exams ?? []).map((e: any) => mapExam(e)));
      setReviews((data.reviews ?? []).map((r: any) => mapReview(r)));
    } catch (err) {
      console.error("[ExplorePageShell] fetchFiltered error:", err);
    }
    setLoading(false);
  }

  // ── Tab filters ──
  const showPaths = activeTab === "all" || activeTab === "paths";
  const showExams = activeTab === "all" || activeTab === "exams";
  const showReviews = activeTab === "all" || activeTab === "articles";

  const totalResults =
    (showPaths ? paths.length : 0) +
    (showExams ? exams.length : 0) +
    (showReviews ? reviews.length : 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <ExploreHeader
        keyword={keyword}
        onKeywordChange={setKeyword}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        sort={sort}
        onSortChange={setSort}
        onOpenMobileFilter={() => setMobileFilterOpen(true)}
        resultCount={totalResults}
      />

      <div className="max-w-7xl mx-auto px-4 py-6 flex gap-6">
        {/* Sidebar filter desktop */}
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-24">
            <ExploreFilterSidebar
              filters={filters}
              onChange={setFilters}
              categories={categories}
            />
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 space-y-8">
          {showPaths && (
            <section>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-bold text-slate-900">Lộ trình học</h2>
                <span className="text-xs text-slate-500">{paths.length} kết quả</span>
              </div>
              <PathsSection items={paths} loading={loading} />
            </section>
          )}

          {showExams && (
            <section>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-bold text-slate-900">Đề thi</h2>
                <span className="text-xs text-slate-500">{exams.length} kết quả</span>
              </div>
             <ExamsSection items={exams} loading={loading} />
            </section>
          )}

          {showReviews && (
            <section>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-bold text-slate-900">Reviews</h2>
                <span className="text-xs text-slate-500">{reviews.length} kết quả</span>
              </div>
              <ReviewsSection items={reviews} loading={loading} />
            </section>
          )}
        </main>
      </div>

      {/* Mobile filter sheet */}
      {mobileFilterOpen && (
        <MobileFilterSheet
          filters={filters}
          onChange={setFilters}
          onClose={() => setMobileFilterOpen(false)}
          categories={categories}
        />
      )}
    </div>
  );
}